import { makeChoices } from "../../distractors";
import { text } from "../../format";
import type { LocalizedText, QuestionDraft, Rng, TopicDef } from "../../types";
import { both, DIV, MUL, numeric, q } from "../helpers";

function expr(parts: (string | LocalizedText)[]): LocalizedText {
  return {
    pl: parts.map((p) => (typeof p === "string" ? p : p.pl)).join(" "),
    en: parts.map((p) => (typeof p === "string" ? p : p.en)).join(" "),
  };
}

function tier1(rng: Rng): QuestionDraft {
  const a = rng.int(2, 30);
  const b = rng.int(2, 9);
  const c = rng.int(2, 9);
  const plus = rng.chance(0.5) || a < b * c;
  const value = plus ? a + b * c : a - b * c;
  const leftToRight = (plus ? a + b : a - b) * c;
  const e = expr([`${a}`, plus ? "+" : "−", `${b}`, MUL, `${c}`]);
  const choice4 = makeChoices(rng, value, [leftToRight, value + 1, value - 1], (r) => r.int(1, 120));
  return q(
    both(`Oblicz: ${e.pl} = ?`, `Calculate: ${e.en} = ?`),
    numeric(value),
    [
      `Najpierw mnożenie: ${b} · ${c} = ${b * c}.`,
      `Potem ${plus ? "dodawanie" : "odejmowanie"}: ${a} ${plus ? "+" : "−"} ${b * c} = ${value}.`,
    ],
    [
      `Multiplication first: ${b} × ${c} = ${b * c}.`,
      `Then ${plus ? "add" : "subtract"}: ${a} ${plus ? "+" : "−"} ${b * c} = ${value}.`,
    ],
    { choice4 },
  );
}

function tier2(rng: Rng): QuestionDraft {
  const a = rng.int(2, 12);
  const b = rng.int(2, 12);
  const c = rng.int(2, 9);
  if (rng.chance(0.5)) {
    const value = (a + b) * c;
    const noBracket = a + b * c;
    const e = expr([`(${a}`, "+", `${b})`, MUL, `${c}`]);
    const choice4 = makeChoices(rng, value, [noBracket, value + c, value - c], (r) => r.int(1, 250));
    return q(
      both(`Oblicz: ${e.pl} = ?`, `Calculate: ${e.en} = ?`),
      numeric(value),
      [`Najpierw nawias: ${a} + ${b} = ${a + b}.`, `Potem mnożenie: ${a + b} · ${c} = ${value}.`],
      [`Brackets first: ${a} + ${b} = ${a + b}.`, `Then multiply: ${a + b} × ${c} = ${value}.`],
      { choice4 },
    );
  }
  const big = Math.max(a, b) + rng.int(1, 5);
  const small = Math.min(a, b);
  const value = c * (big - small);
  const noBracket = c * big - small;
  const e = expr([`${c}`, MUL, `(${big}`, "−", `${small})`]);
  const choice4 = makeChoices(rng, value, [noBracket, value + c, value + small], (r) => r.int(1, 200));
  return q(
    both(`Oblicz: ${e.pl} = ?`, `Calculate: ${e.en} = ?`),
    numeric(value),
    [
      `Najpierw nawias: ${big} − ${small} = ${big - small}.`,
      `Potem mnożenie: ${c} · ${big - small} = ${value}.`,
    ],
    [
      `Brackets first: ${big} − ${small} = ${big - small}.`,
      `Then multiply: ${c} × ${big - small} = ${value}.`,
    ],
    { choice4 },
  );
}

function tier3(rng: Rng): QuestionDraft {
  const c = rng.int(2, 9);
  const k = rng.int(2, 12);
  const sum = c * k; // (a + b) divisible by c
  const a = rng.int(1, sum - 1);
  const b = sum - a;
  const d = rng.int(2, 9);
  const e2 = rng.int(2, 9);
  const plus = rng.chance(0.5);
  const value = plus ? k + d * e2 : k - d * e2;
  const e = expr([`(${a}`, "+", `${b})`, DIV, `${c}`, plus ? "+" : "−", `${d}`, MUL, `${e2}`]);
  const leftToRight = plus ? (k + d) * e2 : (k - d) * e2;
  const choice4 = makeChoices(rng, value, [leftToRight, plus ? k - d * e2 : k + d * e2, value + 1], (r) =>
    r.int(-80, 120),
  );
  return q(
    both(`Oblicz: ${e.pl} = ?`, `Calculate: ${e.en} = ?`),
    numeric(value),
    [
      `Nawias: ${a} + ${b} = ${sum}.`,
      `Dzielenie i mnożenie: ${sum} : ${c} = ${k}, ${d} · ${e2} = ${d * e2}.`,
      `Na końcu: ${k} ${plus ? "+" : "−"} ${d * e2} = ${value}.`,
    ],
    [
      `Brackets: ${a} + ${b} = ${sum}.`,
      `Division and multiplication: ${sum} ÷ ${c} = ${k}, ${d} × ${e2} = ${d * e2}.`,
      `Finally: ${k} ${plus ? "+" : "−"} ${d * e2} = ${value}.`,
    ],
    { choice4 },
  );
}

export const orderOfOps: TopicDef = {
  id: "sp6.order_of_ops",
  level: "sp6",
  name: text("Kolejność działań", "Order of operations"),
  modes: ["numeric", "choice4"],
  generate: (rng, tier) => (tier === 1 ? tier1(rng) : tier === 2 ? tier2(rng) : tier3(rng)),
};
